import {
    IAcademyCourse,
    IAcademyLesson,
    IAcademyLessonStep,
    ICompleteAcademyStepActionPayload,
    ICompleteAcademyStepSuccessActionPayload,
    ILoadAcademyInfoResponse,
    IUpdateAcademyLessonParams
} from "./academy.t";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import jsonParse from "../../utils/jsonParse";
import { ISOString } from "../../types";
import { LOGOUT_SUCCESS } from "../Login/loginConstants";

export interface IAcademyState {
    courses: IAcademyCourse[],
    currentlyCompletedLessonKey: IAcademyLesson['academy_lesson_key'] | null,
    currentStepInfoString: string,
    isInited?: boolean,
    isShowBeginnersGuideInvite: boolean,
    lessons: IAcademyLesson[],
    steps: {
        [key: IAcademyLesson['academy_lesson_key']]: IAcademyLessonStep[],
    }
}

export const initialState: IAcademyState = {
    courses: [],
    currentlyCompletedLessonKey: null,
    currentStepInfoString: '',
    isInited: false,
    isShowBeginnersGuideInvite: false,
    lessons: [],
    steps: {},
}

export const academySlice = createSlice({
    name: 'academy',
    initialState,
    reducers: {
        initAcademyAction() {
        },
        initAcademySuccessAction(state) {
            state.isInited = true;
        },

        loadAcademyInfoAction() {
        },
        loadAcademyInfoSuccessAction(state, action: PayloadAction<ILoadAcademyInfoResponse>) {
            state.courses = action.payload.courses;
            state.lessons = action.payload.lessons.map(lesson => ({
                ...lesson,
                answers: jsonParse(lesson.answers) || {},
                ui_data: jsonParse(lesson.ui_data) || {},
            }));
            Object.entries(action.payload.inProgressLessonsSteps).forEach(([lessonKey, steps]) => {
                state.steps[lessonKey] = steps;
            });
        },
        loadAcademyInfoFailureAction() {
        },

        loadAcademyLessonStepsAction(state, action: PayloadAction<IAcademyLesson['academy_lesson_key']>) {
        },
        loadAcademyLessonStepsSuccessAction(state, action: PayloadAction<IAcademyLessonStep[]>) {
            if (action.payload.length > 0) {
                state.steps[action.payload[0].academy_lesson_key] = action.payload;
            }
        },
        loadAcademyLessonStepsFailureAction() {
        },

        completeAcademyLessonStepAction(state, action: PayloadAction<ICompleteAcademyStepActionPayload>) {
        },
        completeAcademyLessonStepSuccessAction(state, action: PayloadAction<ICompleteAcademyStepSuccessActionPayload>) {
            const answerStepKey = action.payload.answerStepKey;
            const lessonKey = action.payload.lessonKey;
            const lastCompletedStepKey = action.payload.lastCompletedStepKey;
            state.lessons = state.lessons.map(lesson => {
                if (lesson.academy_lesson_key === lessonKey) {
                    const answers = answerStepKey
                        ? {
                            ...lesson.answers,
                            [lastCompletedStepKey]: answerStepKey,
                        }
                        : lesson.answers;
                    return {
                        ...lesson,
                        answers,
                        completed_at: action.payload.isLessonLastStep
                            ? new Date().toISOString() as ISOString
                            : lesson.completed_at,
                        last_completed_step_key: lastCompletedStepKey,
                    };
                }
                return lesson;
            });
            if (action.payload.isLessonLastStep) {
                state.currentlyCompletedLessonKey = lessonKey;
            }
            state.currentStepInfoString = '';
        },

        onUnmountLessonAction(state, action: PayloadAction<IAcademyLesson['academy_lesson_key']>) {
            state.currentlyCompletedLessonKey = null;
        },

        setCurrentStepInfoStringAction(state, action: PayloadAction<string>) {
            state.currentStepInfoString = action.payload;
        },

        setIsShowBeginnersGuideInviteAction(state, action: PayloadAction<boolean>) {
            state.isShowBeginnersGuideInvite = action.payload;
        },

        updateUserAcademyLessonAction(state, action: PayloadAction<IUpdateAcademyLessonParams>) {
            state.lessons = state.lessons.map(lesson => {
                if (lesson.academy_lesson_key === action.payload.lessonKey) {
                    const params: Partial<IAcademyLesson> = {}
                    if (action.payload.lastCompletedStepKey !== undefined) {
                        params.last_completed_step_key = action.payload.lastCompletedStepKey
                    }
                    if (action.payload.uiData !== undefined) {
                        params.ui_data = action.payload.uiData
                    }
                    return {
                        ...lesson,
                        ...params,
                    };
                }
                return lesson;
            });
        }
    },
    extraReducers: (builder) => {
        builder.addCase(LOGOUT_SUCCESS, (state) => {
            return {
                ...initialState,
            }
        })
    },
});

export default academySlice.reducer;
